import { z } from "zod";

import {
  CreatePersonRequestSchema,
  UPN_SYSTEM,
  type CreatePersonRequestUnbranded,
  type PersonMatchRequest,
} from "@thalamos/haven-common";

type Known = "" | "yes" | "no";

/** What the registration form holds while somebody fills it in. */
export type CreatePersonValues = {
  given: string;
  family: string;
  dateOfBirthKnown: Known;
  dateOfBirth: string;
  homeAddressKnown: Known;
  address: string;
  postcode: string;
  upn: string;
};

export const emptyCreatePersonValues = (): CreatePersonValues => ({
  given: "",
  family: "",
  dateOfBirthKnown: "",
  dateOfBirth: "",
  homeAddressKnown: "",
  address: "",
  postcode: "",
  upn: "",
});

type FormIssue = { message: string; path: PropertyKey[] };

const splitGiven = (text: string): string[] =>
  text.split(/\s+/).filter((part) => part.length > 0);

const formIssues = (values: CreatePersonValues): FormIssue[] =>
  [
    splitGiven(values.given).length === 0
      ? { message: "personPage.creation.given.required", path: ["given"] }
      : null,
    values.family.trim() === ""
      ? {
          message: "personPage.creation.family.required",
          path: ["family"],
        }
      : null,
    values.dateOfBirthKnown === ""
      ? {
          message: "personPage.creation.dateOfBirthKnown.required",
          path: ["dateOfBirthKnown"],
        }
      : null,
    values.dateOfBirthKnown === "yes" && values.dateOfBirth === ""
      ? {
          message: "personPage.creation.dateOfBirth.required",
          path: ["dateOfBirth"],
        }
      : null,
    values.homeAddressKnown === ""
      ? {
          message: "personPage.creation.homeAddressKnown.required",
          path: ["homeAddressKnown"],
        }
      : null,
  ].filter((issue) => issue !== null);

/**
 * The form field a registration issue belongs to. `null` for the request's
 * rule that a name has both parts, which `formIssues` states field by field.
 */
const fieldOf = (path: readonly PropertyKey[]): PropertyKey[] | null => {
  const [head, second, third] = path;

  if (head === "names") {
    return third === "given" || third === "family" ? [third] : null;
  }
  if (head === "homeAddress") {
    return second === undefined ? [] : [second];
  }
  if (head === "identifiers") {
    return ["upn"];
  }

  return [...path];
};

const toRequest = (
  values: CreatePersonValues,
): CreatePersonRequestUnbranded => {
  const upn = values.upn.trim();

  return {
    names: [
      {
        use: "official",
        family: values.family === "" ? null : values.family,
        given: splitGiven(values.given),
        periodStart: null,
        periodEnd: null,
      },
    ],
    dateOfBirth: values.dateOfBirthKnown === "yes" ? values.dateOfBirth : null,
    homeAddress:
      values.homeAddressKnown === "yes"
        ? { address: values.address, postcode: values.postcode }
        : null,
    identifiers: upn === "" ? [] : [{ system: UPN_SYSTEM, value: upn }],
  };
};

/**
 * Reads the form into the registration the server is asked to make, so the form
 * and the server refuse the same things with the same messages. Only the
 * questions the request cannot express — whether a date of birth or an address
 * is known — and the names, which the form asks for a part at a time, are the
 * form's own.
 */
export const CreatePersonValuesSchema = z
  .object({
    given: z.string(),
    family: z.string(),
    dateOfBirthKnown: z.enum(["", "yes", "no"]),
    dateOfBirth: z.string(),
    homeAddressKnown: z.enum(["", "yes", "no"]),
    address: z.string(),
    postcode: z.string(),
    upn: z.string(),
  })
  .transform((values, context) => {
    const parsed = CreatePersonRequestSchema.safeParse(toRequest(values));
    const own = formIssues(values);
    const flagged = new Set(own.map((issue) => issue.path.join(".")));
    // A field the form has already asked about keeps the form's message, which
    // says what to do in the words of the question.
    const requestIssues = parsed.success
      ? []
      : parsed.error.issues.flatMap((issue) => {
          const path = fieldOf(issue.path);
          return path === null || flagged.has(path.join("."))
            ? []
            : [{ message: issue.message, path }];
        });
    const issues = [...own, ...requestIssues];

    for (const issue of issues) {
      context.addIssue({
        code: "custom",
        message: issue.message,
        path: issue.path,
      });
    }

    return parsed.success && issues.length === 0 ? parsed.data : z.NEVER;
  });

/**
 * The form as a search left it, so what was searched for is not typed again.
 * Not knowing a date of birth when searching says nothing about whether it is
 * known, so that question is left for the person registering.
 */
export const valuesFromHandOff = (
  request: PersonMatchRequest,
): CreatePersonValues =>
  request.type === "upn"
    ? { ...emptyCreatePersonValues(), upn: request.upn }
    : {
        ...emptyCreatePersonValues(),
        family: request.family,
        given: request.given ?? "",
        dateOfBirthKnown: request.dateOfBirth === null ? "" : "yes",
        dateOfBirth: request.dateOfBirth ?? "",
      };
